// Offline Database Service using IndexedDB
// Handles local storage for tasks, evidences, and sync queue

const DB_NAME = 'kaizenos_offline'
const DB_VERSION = 1

const STORES = {
  TASKS: 'tasks',
  EVIDENCES: 'evidences',
  SYNC_QUEUE: 'sync_queue',
  META: 'meta'
}

class OfflineDB {
  constructor() {
    this.db = null
    this.isSupported = 'indexedDB' in window
  }

  async init() {
    if (!this.isSupported) {
      console.warn('IndexedDB not supported')
      return false
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('Failed to open IndexedDB')
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(true)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result

        // Tasks store
        if (!db.objectStoreNames.contains(STORES.TASKS)) {
          const tasksStore = db.createObjectStore(STORES.TASKS, { keyPath: 'id' })
          tasksStore.createIndex('user_id', 'user_id', { unique: false })
          tasksStore.createIndex('sync_status', 'sync_status', { unique: false })
          tasksStore.createIndex('due_date', 'due_date', { unique: false })
        }

        // Evidences store (stores image blobs)
        if (!db.objectStoreNames.contains(STORES.EVIDENCES)) {
          const evidencesStore = db.createObjectStore(STORES.EVIDENCES, { keyPath: 'local_id' })
          evidencesStore.createIndex('task_id', 'task_id', { unique: false })
          evidencesStore.createIndex('evidence_status', 'evidence_status', { unique: false })
        }

        // Sync queue store
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true })
          syncStore.createIndex('type', 'type', { unique: false })
          syncStore.createIndex('created_at', 'created_at', { unique: false })
          syncStore.createIndex('status', 'status', { unique: false })
        }

        // Metadata store (last sync time, etc)
        if (!db.objectStoreNames.contains(STORES.META)) {
          db.createObjectStore(STORES.META, { keyPath: 'key' })
        }
      }
    })
  }

  // ==========================================
  // TASKS OPERATIONS
  // ==========================================

  async saveTask(task) {
    if (!this.db) return null
    
    const taskWithMeta = {
      ...task,
      sync_status: task.sync_status || 'synced',
      last_local_update_at: new Date().toISOString(),
      last_synced_at: task.last_synced_at || new Date().toISOString()
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.TASKS, 'readwrite')
      const store = tx.objectStore(STORES.TASKS)
      const request = store.put(taskWithMeta)
      
      request.onsuccess = () => resolve(taskWithMeta)
      request.onerror = () => reject(request.error)
    })
  }

  async saveTasks(tasks) {
    if (!this.db) return
    
    const tx = this.db.transaction(STORES.TASKS, 'readwrite')
    const store = tx.objectStore(STORES.TASKS)
    
    for (const task of tasks) {
      const taskWithMeta = {
        ...task,
        sync_status: 'synced',
        last_local_update_at: new Date().toISOString(),
        last_synced_at: new Date().toISOString()
      }
      store.put(taskWithMeta)
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }

  async getTask(id) {
    if (!this.db) return null

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.TASKS, 'readonly')
      const store = tx.objectStore(STORES.TASKS)
      const request = store.get(id)
      
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getAllTasks() {
    if (!this.db) return []

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.TASKS, 'readonly')
      const store = tx.objectStore(STORES.TASKS)
      const request = store.getAll()
      
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async getPendingTasks() {
    if (!this.db) return []

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.TASKS, 'readonly')
      const store = tx.objectStore(STORES.TASKS)
      const index = store.index('sync_status')
      const request = index.getAll('pending')
      
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async deleteTask(id) {
    if (!this.db) return

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.TASKS, 'readwrite')
      const store = tx.objectStore(STORES.TASKS)
      const request = store.delete(id)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async updateTaskSyncStatus(id, status, serverData = null) {
    const task = await this.getTask(id)
    if (!task) return null

    const updatedTask = {
      ...task,
      ...(serverData || {}),
      sync_status: status,
      last_synced_at: status === 'synced' ? new Date().toISOString() : task.last_synced_at
    }

    return this.saveTask(updatedTask)
  }

  // ==========================================
  // EVIDENCES OPERATIONS
  // ==========================================

  async saveEvidence(evidence) {
    if (!this.db) return null

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.EVIDENCES, 'readwrite')
      const store = tx.objectStore(STORES.EVIDENCES)
      const request = store.put(evidence)
      
      request.onsuccess = () => resolve(evidence)
      request.onerror = () => reject(request.error)
    })
  }

  async getEvidencesByTask(taskId) {
    if (!this.db) return []

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.EVIDENCES, 'readonly')
      const store = tx.objectStore(STORES.EVIDENCES)
      const index = store.index('task_id')
      const request = index.getAll(taskId)
      
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async getPendingEvidences() {
    if (!this.db) return []

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.EVIDENCES, 'readonly')
      const store = tx.objectStore(STORES.EVIDENCES)
      const index = store.index('evidence_status')
      const request = index.getAll('local_only')
      
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async deleteEvidence(localId) {
    if (!this.db) return

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.EVIDENCES, 'readwrite')
      const store = tx.objectStore(STORES.EVIDENCES)
      const request = store.delete(localId)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async updateEvidenceStatus(localId, status, remoteData = null) {
    if (!this.db) return null

    const evidence = await this.getEvidence(localId)
    if (!evidence) return null

    const updated = {
      ...evidence,
      ...(remoteData || {}),
      evidence_status: status
    }

    return this.saveEvidence(updated)
  }

  async getEvidence(localId) {
    if (!this.db) return null

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.EVIDENCES, 'readonly')
      const store = tx.objectStore(STORES.EVIDENCES)
      const request = store.get(localId)
      
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================
  // SYNC QUEUE OPERATIONS
  // ==========================================

  async addToSyncQueue(item) {
    if (!this.db) return null

    const queueItem = {
      ...item,
      created_at: new Date().toISOString(),
      status: 'pending',
      retries: 0
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite')
      const store = tx.objectStore(STORES.SYNC_QUEUE)
      const request = store.add(queueItem)
      
      request.onsuccess = () => {
        queueItem.id = request.result
        resolve(queueItem)
      }
      request.onerror = () => reject(request.error)
    })
  }

  async getSyncQueue() {
    if (!this.db) return []

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readonly')
      const store = tx.objectStore(STORES.SYNC_QUEUE)
      const index = store.index('created_at')
      const request = index.getAll()
      
      request.onsuccess = () => {
        const items = request.result || []
        // Filter only pending items and sort by created_at
        const pending = items
          .filter(i => i.status === 'pending' || i.status === 'failed')
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        resolve(pending)
      }
      request.onerror = () => reject(request.error)
    })
  }

  async updateSyncQueueItem(id, updates) {
    if (!this.db) return null

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite')
      const store = tx.objectStore(STORES.SYNC_QUEUE)
      const getRequest = store.get(id)
      
      getRequest.onsuccess = () => {
        const item = getRequest.result
        if (!item) {
          resolve(null)
          return
        }
        
        const updated = { ...item, ...updates }
        const putRequest = store.put(updated)
        
        putRequest.onsuccess = () => resolve(updated)
        putRequest.onerror = () => reject(putRequest.error)
      }
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  async removeSyncQueueItem(id) {
    if (!this.db) return

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite')
      const store = tx.objectStore(STORES.SYNC_QUEUE)
      const request = store.delete(id)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clearCompletedSyncItems() {
    if (!this.db) return

    const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.SYNC_QUEUE)
    const index = store.index('status')
    const request = index.openCursor(IDBKeyRange.only('completed'))

    request.onsuccess = (event) => {
      const cursor = event.target.result
      if (cursor) {
        store.delete(cursor.primaryKey)
        cursor.continue()
      }
    }
  }

  // ==========================================
  // METADATA OPERATIONS
  // ==========================================

  async setMeta(key, value) {
    if (!this.db) return

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.META, 'readwrite')
      const store = tx.objectStore(STORES.META)
      const request = store.put({ key, value, updated_at: new Date().toISOString() })
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getMeta(key) {
    if (!this.db) return null

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.META, 'readonly')
      const store = tx.objectStore(STORES.META)
      const request = store.get(key)
      
      request.onsuccess = () => resolve(request.result?.value)
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  async clearAll() {
    if (!this.db) return

    const stores = [STORES.TASKS, STORES.EVIDENCES, STORES.SYNC_QUEUE]
    
    for (const storeName of stores) {
      const tx = this.db.transaction(storeName, 'readwrite')
      tx.objectStore(storeName).clear()
    }
  }

  async getStats() {
    const tasks = await this.getAllTasks()
    const pendingTasks = await this.getPendingTasks()
    const pendingEvidences = await this.getPendingEvidences()
    const syncQueue = await this.getSyncQueue()

    return {
      totalTasks: tasks.length,
      pendingTasks: pendingTasks.length,
      pendingEvidences: pendingEvidences.length,
      syncQueueSize: syncQueue.length
    }
  }
}

// Singleton instance
export const offlineDB = new OfflineDB()
export default offlineDB
